import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
  loadUserProfileSuccess,
  loadUsersSuccess,
  connectedToGraphSuccess,
  downloadSignatureSuccess,
  loadUserSubscriptionDetailsSuccess,
  loadUserSubscriptionTypeSuccess,
  loadUserSubscriptionTypeError,
  setIsUserOnPricingPage,
  setTrialDaysLeft,
  loadTrialDurationInDaysSuccess,
  loadCreditsLimitationsSuccess,
  loadCreditsMaxLimitSuccess,
  loadUsersForAdminSuccess,
} from './users.actions';
import { Pagination } from '@app/shared/models/pagination';
import { SubscriptionDetails } from '@app/shared/models/subscription-details';
import { OwnerUsers, User } from '../../shared/models/user';
import { SubscriptionType } from '../../shared/models/subscription-type';
import { CreditsInfo } from '@app/shared/models/credits-info';
import { MaxCredits } from '@app/shared/models/max-credits';

export interface UsersState extends EntityState<User> {
  loggedInUser: User | null;
  usersPagination: Pagination<User> | null;
  signature: string;
  trialDaysLeft: number | null;
  subscriptionType: SubscriptionType | null;
  subscriptionTypeLoaded: boolean;
  subscriptionDetails: SubscriptionDetails | null;
  isUserOnPricingPage: boolean;
  trialDurationInDays: number | null;
  creditsLimitations: CreditsInfo | null;
  creditsMaxLimit: MaxCredits | null;
  ownerUsers: OwnerUsers | null;
}

export const usersAdapter: EntityAdapter<User> = createEntityAdapter<User>();

export const usersInitialState: UsersState = usersAdapter.getInitialState({
  loggedInUser: null,
  signature: '',
  usersPagination: null,
  trialDaysLeft: null,
  subscriptionType: null,
  subscriptionTypeLoaded: false,
  subscriptionDetails: null,
  isUserOnPricingPage: false,
  trialDurationInDays: null,
  creditsLimitations: null,
  creditsMaxLimit: null,
  ownerUsers: null,
});

export const usersReducer = createReducer(
  usersInitialState,
  on(loadUserProfileSuccess, (state, { payload }) => {
    return {
      ...state,
      loggedInUser: {
        ...payload.user,
        connectedToGraph: payload.user.connectedToGraph,
      },
    };
  }),
  on(loadUsersSuccess, (state, { usersPagination }) => {
    return usersAdapter.setAll(usersPagination.content, {
      ...state,
      usersPagination: usersPagination,
    });
  }),
  on(connectedToGraphSuccess, (state, { connectedToGraph }) => {
    return {
      ...state,
      loggedInUser: state.loggedInUser
        ? {
            ...state.loggedInUser,
            connectedToGraph,
          }
        : null,
    };
  }),
  on(downloadSignatureSuccess, (state, { signature }) => {
    return {
      ...state,
      signature,
    };
  }),
  on(loadUserSubscriptionTypeSuccess, (state, { payload }) => ({
    ...state,
    subscriptionType: payload.subscriptionType,
    subscriptionTypeLoaded: true,
  })),
  on(loadUserSubscriptionTypeError, (state) => ({
    ...state,
    subscriptionType: null,
    subscriptionTypeLoaded: true,
  })),
  on(loadUserSubscriptionDetailsSuccess, (state, { payload }) => ({
    ...state,
    subscriptionDetails: payload.subscriptionDetails,
  })),
  on(setTrialDaysLeft, (state, { trialDaysLeft }) => ({
    ...state,
    trialDaysLeft,
  })),
  on(setIsUserOnPricingPage, (state, { isUserOnPricingPage }) => ({
    ...state,
    isUserOnPricingPage,
  })),
  on(loadTrialDurationInDaysSuccess, (state, { payload }) => ({
    ...state,
    trialDurationInDays: payload.trialDurationInDays,
  })),
  on(loadCreditsLimitationsSuccess, (state, { payload }) => ({
    ...state,
    creditsLimitations: payload.creditsLimitations,
  })),
  on(loadCreditsMaxLimitSuccess, (state, { payload }) => ({
    ...state,
    creditsMaxLimit: payload.creditsMaxLimit,
  })),
  on(loadUsersForAdminSuccess, (state, { payload }) => ({
    ...state,
    ownerUsers: payload.users,
  })),
);
